import { CalendarDays, MapPin, Users } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import NextArrow from "@/components/NextArrow";
import { useLanguage } from "@/i18n/LanguageContext";

const L = {
  ar: {
    soon: "قريبًا",
    done: "مُنفَّذة",
    audience: "الفئة",
    date: "الموعد",
    location: "المكان",
  },
  en: {
    soon: "Coming soon",
    done: "Delivered",
    audience: "Audience",
    date: "Date",
    location: "Location",
  },
};

/**
 * Minimal, structured workshop card.
 * Props:
 *  - status: "upcoming" | "past" (controls chip tone)
 *  - format: localized label of delivery format
 *  - title, theme, audience, date, location
 *  - ctaLabel, ctaHref, ctaTo (optional; `ctaTo` is a locale-relative path)
 */
export default function WorkshopCard({
  status = "upcoming",
  format,
  title,
  theme,
  audience,
  date,
  location,
  ctaLabel,
  ctaHref,
  ctaTo,
  testId,
}) {
  const { lang } = useLanguage();
  const l = L[lang];

  const Cta =
    ctaTo ? (
      <LocalizedLink to={ctaTo} className="sas-btn-link mt-auto inline-flex">
        {ctaLabel} <NextArrow size={15} />
      </LocalizedLink>
    ) : ctaHref ? (
      <a href={ctaHref} className="sas-btn-link mt-auto inline-flex">
        {ctaLabel} <NextArrow size={15} />
      </a>
    ) : null;

  return (
    <article className="sas-card flex flex-col" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <span className={status === "past" ? "sas-chip sas-chip-neutral" : "sas-chip"}>
          {format}
        </span>
        {status === "upcoming" && (
          <span className="text-xs text-[color:var(--sas-ink-2)]">{l.soon}</span>
        )}
        {status === "past" && (
          <span className="text-xs text-[color:var(--sas-ink-2)]">{l.done}</span>
        )}
      </div>

      <h3 className="sas-h3">{title}</h3>
      {theme && <p className="sas-body mt-2 text-[15px]">{theme}</p>}

      <dl className="mt-5 space-y-2.5 text-sm">
        {audience && (
          <div className="flex items-start gap-2.5">
            <Users size={15} className="mt-0.5 text-[color:var(--sas-ink-2)] shrink-0" aria-hidden />
            <dt className="sr-only">{l.audience}</dt>
            <dd>{audience}</dd>
          </div>
        )}
        {date && (
          <div className="flex items-start gap-2.5">
            <CalendarDays size={15} className="mt-0.5 text-[color:var(--sas-ink-2)] shrink-0" aria-hidden />
            <dt className="sr-only">{l.date}</dt>
            <dd>{date}</dd>
          </div>
        )}
        {location && (
          <div className="flex items-start gap-2.5">
            <MapPin size={15} className="mt-0.5 text-[color:var(--sas-ink-2)] shrink-0" aria-hidden />
            <dt className="sr-only">{l.location}</dt>
            <dd>{location}</dd>
          </div>
        )}
      </dl>

      {Cta && <div className="mt-6">{Cta}</div>}
    </article>
  );
}
